import { Link } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { isUiMockMode } from "@/config/features";
import { useCreateExport } from "@/features/invoices/hooks/useCreateExport";
import { ApiErrorNotice } from "@/features/billing/components/ApiErrorNotice";

interface ExportModalProps {
  open: boolean;
  invoiceIds: string[];
  /** Defaults to invoice_pdf. Use receipt or credit_note on dedicated pages. */
  documentType?: "invoice_pdf" | "receipt" | "credit_note";
  title?: string;
  onClose: () => void;
  onStarted: (jobId: string) => void;
}

export function ExportModal({
  open,
  invoiceIds,
  documentType = "invoice_pdf",
  title = "Export invoice PDFs",
  onClose,
  onStarted,
}: ExportModalProps) {
  const create = useCreateExport();
  const mock = isUiMockMode();

  if (!open) return null;

  const exportableCount = invoiceIds.length;

  const onSubmit = () => {
    if (exportableCount === 0) return;
    if (mock) {
      onClose();
      onStarted("job_mock_ui_preview");
      return;
    }
    create.mutate(
      {
        resource_ids: invoiceIds,
        document_type: documentType,
      },
      {
        onSuccess: (res) => {
          onClose();
          if (res.job_id) onStarted(res.job_id);
        },
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-lg">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="mt-2 text-sm text-[var(--muted)]">
          <strong className="tabular-nums text-[var(--text)]">
            {exportableCount}
          </strong>{" "}
          selected · runs in background
        </p>

        <p className="mt-4 text-sm text-[var(--muted)]">
          Files are packaged as a single ZIP.{" "}
          {mock ? (
            <>Mock mode — export job is not sent to the API.</>
          ) : (
            <>
              Track progress and download from{" "}
              <Link to={ROUTES.exports} className="font-semibold text-[var(--accent)]">
                Export History
              </Link>
              .
            </>
          )}
        </p>

        {create.error ? (
          <ApiErrorNotice error={create.error} context="export" />
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-semibold"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
            disabled={exportableCount === 0 || create.isPending}
            onClick={onSubmit}
          >
            {create.isPending
              ? "Starting…"
              : `Start export (${exportableCount})`}
          </button>
        </div>
      </div>
    </div>
  );
}
